import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, Space, Table, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import {
  deactivateInvoicingProduct,
  fetchInvoicingProducts
} from 'services/api/invoicingProducts';
import { INVOICE_SERVICE_TYPE_LABELS } from 'constants/invoicing';
import { InvoicingProduct } from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import { money } from 'utils/price';
import ProductModal from './components/ProductModal';

const InvoicingProducts: FC = () => {
  const [products, setProducts] = useState<InvoicingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InvoicingProduct | null>(null);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const rows = await fetchInvoicingProducts();
        if (!cancelled) setProducts(rows);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((product: InvoicingProduct) => {
    setEditing(product);
    setModalOpen(true);
  }, []);

  const onDeactivate = useCallback(
    async (product: InvoicingProduct) => {
      try {
        await deactivateInvoicingProduct(product.publicId);
        reload();
      } catch (err) {
        showErrorMessage(err);
      }
    },
    [reload]
  );

  const columns: ColumnProps<InvoicingProduct>[] = useMemo(
    () => [
      { title: 'Name (EN)', dataIndex: 'nameEn', key: 'nameEn' },
      { title: 'Name (DE)', dataIndex: 'nameDe', key: 'nameDe' },
      {
        title: 'Service type',
        dataIndex: 'serviceType',
        key: 'serviceType',
        render: (value: InvoicingProduct['serviceType']) =>
          INVOICE_SERVICE_TYPE_LABELS[value] ?? value
      },
      {
        title: 'Default cost',
        dataIndex: 'defaultCost',
        key: 'defaultCost',
        align: 'right',
        render: (value: string, row) => money(value, { currency: row.currency })
      },
      {
        title: 'Tax rate',
        dataIndex: 'defaultTaxRate',
        key: 'defaultTaxRate',
        align: 'right',
        render: (value: string) => `${Number(value)}%`
      },
      {
        title: 'Status',
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) =>
          active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string) => dayjs(value).format(FULL_DATE_FORMAT)
      },
      {
        title: '',
        key: 'actions',
        align: 'right',
        render: (_, row) => (
          <Space>
            <Button size="small" onClick={() => openEdit(row)}>
              Edit
            </Button>
            {row.active && (
              <Popconfirm
                title="Deactivate this product?"
                okText="Deactivate"
                okButtonProps={{ danger: true }}
                onConfirm={() => onDeactivate(row)}
              >
                <Button size="small" danger>
                  Deactivate
                </Button>
              </Popconfirm>
            )}
          </Space>
        )
      }
    ],
    [openEdit, onDeactivate]
  );

  return (
    <PageWrapper
      title="Products"
      color="white"
      extra={[
        <Button
          key="add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          Add product
        </Button>
      ]}
    >
      <Table
        dataSource={products}
        columns={columns}
        loading={loading}
        rowKey="publicId"
        pagination={false}
      />
      <ProductModal
        open={modalOpen}
        product={editing}
        onClose={() => setModalOpen(false)}
        onSuccess={reload}
      />
    </PageWrapper>
  );
};

export default InvoicingProducts;
